/**
 * Working out whether we are looking at a petrol or a diesel engine.
 *
 * It matters: the consumption maths uses a different stoichiometric ratio and
 * fuel density for each, and diesels need a modelled lambda where petrols can
 * assume closed-loop stoichiometric running.
 *
 * Three sources, in descending order of trust: the ECU says so (PID 0x51), the
 * set of supported PIDs implies it, or live readings imply it.
 */
import type { FuelType } from '../types.js';
import type { SignalStore } from '../signals/store.js';

export interface FuelInference {
  type: FuelType;
  /** 0..1. Anything below ~0.5 should be treated as a guess. */
  confidence: number;
  reasons: string[];
}

/** PID 0x51 enumeration from J1979. */
const FUEL_TYPE_CODES: Record<number, FuelType> = {
  1: 'petrol',
  2: 'petrol', // methanol -- petrol maths is the closest of what we model
  3: 'ethanol',
  4: 'diesel',
  5: 'lpg',
  6: 'cng',
  7: 'lpg', // propane
  8: 'electric',
  9: 'petrol',
  10: 'petrol',
  11: 'ethanol',
  12: 'lpg',
  13: 'cng',
  14: 'lpg',
  15: 'electric',
  16: 'hybrid',
  17: 'hybrid',
  18: 'hybrid',
  19: 'diesel', // hybrid diesel: the combustion side is what we meter
  20: 'electric',
  21: 'hybrid',
  22: 'hybrid',
  23: 'diesel',
};

/** PIDs that essentially only exist on compression-ignition engines. */
const DIESEL_MARKERS: { pid: number; weight: number; why: string }[] = [
  { pid: 0x23, weight: 3, why: 'fuel rail gauge pressure (common rail)' },
  { pid: 0x59, weight: 2, why: 'fuel rail absolute pressure' },
  { pid: 0x5d, weight: 2, why: 'fuel injection timing' },
  { pid: 0x78, weight: 3, why: 'exhaust gas temperature bank 1' },
  { pid: 0x79, weight: 2, why: 'exhaust gas temperature bank 2' },
  { pid: 0x7a, weight: 4, why: 'diesel particulate filter pressures' },
  { pid: 0x7c, weight: 4, why: 'diesel particulate filter temperature' },
  { pid: 0x83, weight: 3, why: 'NOx sensor' },
  { pid: 0x9e, weight: 2, why: 'exhaust flow rate' },
  { pid: 0xa2, weight: 3, why: 'cylinder fuel rate (mg/stroke)' },
  { pid: 0x73, weight: 1, why: 'exhaust pressure' },
];

/** PIDs that only make sense with a spark-ignition, throttled engine. */
const PETROL_MARKERS: { pid: number; weight: number; why: string }[] = [
  { pid: 0x0e, weight: 3, why: 'ignition timing advance' },
  { pid: 0x2e, weight: 3, why: 'evaporative purge control' },
  { pid: 0x32, weight: 2, why: 'evap system vapour pressure' },
  { pid: 0x53, weight: 2, why: 'absolute evap vapour pressure' },
  { pid: 0x3c, weight: 2, why: 'catalyst temperature' },
  { pid: 0x14, weight: 2, why: 'narrowband oxygen sensor' },
  { pid: 0x15, weight: 1, why: 'second narrowband oxygen sensor' },
  { pid: 0x06, weight: 1, why: 'short term fuel trim' },
  { pid: 0x44, weight: 1, why: 'commanded lambda' },
  { pid: 0x52, weight: 1, why: 'ethanol content' },
];

/**
 * Combine every available clue into one verdict.
 *
 * `supported` is the set of mode 0x01 PIDs the vehicle reported; `store` is
 * consulted for live values, which can settle cases the PID list alone cannot.
 */
export function inferFuelType(supported: ReadonlySet<number>, store: SignalStore): FuelInference {
  const reasons: string[] = [];

  // ---- 1. The ECU's own declaration, when it makes one -----------------------
  const declared = store.value('fuel_type_code');
  if (declared !== undefined && declared > 0) {
    const mapped = FUEL_TYPE_CODES[declared];
    if (mapped) {
      return {
        type: mapped,
        confidence: 0.97,
        reasons: ['ECU reports fuel type code ' + declared + ' via PID 0x51'],
      };
    }
  }

  // ---- 2. What the supported-PID list implies --------------------------------
  let dieselScore = 0;
  let petrolScore = 0;
  for (const m of DIESEL_MARKERS) {
    if (supported.has(m.pid)) {
      dieselScore += m.weight;
      reasons.push('Supports ' + m.why);
    }
  }
  for (const m of PETROL_MARKERS) {
    if (supported.has(m.pid)) {
      petrolScore += m.weight;
      reasons.push('Supports ' + m.why);
    }
  }

  // ---- 3. Live readings that are hard to argue with --------------------------
  const rail = store.value('fuel_rail_pressure') ?? store.value('fuel_rail_abs_pressure');
  if (rail !== undefined && rail > 60000) {
    // Direct-injection petrol tops out near 350 bar; common-rail diesel runs
    // well past 1000 bar, so this threshold separates them cleanly.
    dieselScore += 5;
    reasons.push('Rail pressure of ' + Math.round(rail / 100) + ' bar is beyond petrol direct injection');
  }
  const timing = store.get('timing_advance');
  if (timing?.available && timing.stats.samples > 5 && timing.stats.max !== timing.stats.min) {
    petrolScore += 3;
    reasons.push('Ignition timing advance is live and varying');
  }
  const maxRpm = store.get('rpm')?.stats.max;
  if (maxRpm !== undefined && Number.isFinite(maxRpm)) {
    if (maxRpm > 6200) {
      petrolScore += 2;
      reasons.push('Engine has been seen above 6200 rpm');
    } else if (maxRpm > 2500 && maxRpm < 4800) {
      dieselScore += 1;
      reasons.push('Engine speed range looks like a diesel');
    }
  }

  const total = dieselScore + petrolScore;
  if (total === 0) {
    return { type: 'unknown', confidence: 0, reasons: ['Not enough evidence yet'] };
  }

  const diesel = dieselScore > petrolScore;
  const winner = Math.max(dieselScore, petrolScore);
  // Confidence reflects both the margin and how much evidence there was at all.
  const margin = winner / total;
  const volume = Math.min(total / 12, 1);
  const confidence = Math.min(0.95, margin * volume);

  return {
    type: diesel ? 'diesel' : 'petrol',
    confidence,
    reasons: reasons.slice(0, 8),
  };
}

/** Sensible defaults to pre-fill the vehicle profile once fuel type is known. */
export function defaultsForFuelType(type: FuelType): { redlineRpm: number; ecoShiftRpm: number; volumetricEfficiency: number } {
  switch (type) {
    case 'diesel':
      return { redlineRpm: 4800, ecoShiftRpm: 2000, volumetricEfficiency: 0.9 };
    case 'hybrid':
      return { redlineRpm: 6000, ecoShiftRpm: 2200, volumetricEfficiency: 0.85 };
    default:
      return { redlineRpm: 6500, ecoShiftRpm: 2500, volumetricEfficiency: 0.85 };
  }
}
